/**
 * <bundle-builder> — "Build Your Bundle" buy box (ported from Bundle Builder
 * Pro). Tier per-bar prices are display values; the real discount is applied by
 * the bundle app at checkout. Adds the selected quantity (and selling plan when
 * subscribing) to the cart.
 */
class BundleBuilder extends HTMLElement {
  constructor() {
    super();
    try {
      const node = this.querySelector('[data-bb-tiers]');
      this.tiers = node ? JSON.parse(node.textContent) : [];
    } catch (e) {
      this.tiers = [];
    }
    this.tiers.sort((a, b) => a.qty - b.qty);

    this.variantId = this.dataset.variantId;
    this.basePrice = parseInt(this.dataset.basePrice, 10) || 0;
    this.barsPerBox = parseInt(this.dataset.barsPerBox, 10) || 10;
    this.subExtra = parseFloat(this.dataset.subExtra) || 0;
    this.sellingPlanId = this.dataset.sellingPlanId || '';
    this.productId = this.dataset.productId || '';
    this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';
    this.atcText = this.dataset.atcText || 'Add to Cart';

    this.maxQty = this.tiers.length ? this.tiers[this.tiers.length - 1].qty : 1;
    this.plan = this.dataset.subscribeDefault === 'true' && this.sellingPlanId ? 'sub' : 'once';
    this.qty = this.tiers[0] ? this.tiers[0].qty : 1;

    this.els = {
      steps: this.querySelector('[data-bb-steps]'),
      nudge: this.querySelector('[data-bb-nudge]'),
      grid: this.querySelector('[data-bb-tiers-grid]'),
      qty: this.querySelector('[data-bb-qty]'),
      qtyLabel: this.querySelector('[data-bb-qty-label]'),
      subTotal: this.querySelector('[data-bb-sub-total]'),
      onceTotal: this.querySelector('[data-bb-once-total]'),
      bars: this.querySelector('[data-bb-bars]'),
      subtotal: this.querySelector('[data-bb-subtotal]'),
      savings: this.querySelector('[data-bb-savings]'),
      total: this.querySelector('[data-bb-total]'),
      perbar: this.querySelector('[data-bb-perbar]'),
      atc: this.querySelector('[data-bb-atc]'),
      atcLabel: this.querySelector('[data-bb-atc-label]'),
      spinner: this.querySelector('[data-bb-spinner]'),
      error: this.querySelector('[data-bb-error]'),
    };
  }

  connectedCallback() {
    if (!this.sellingPlanId) {
      // No subscription available — hide the subscribe plan, default to one-time.
      this.querySelector('[data-bb-plan="sub"]')?.setAttribute('hidden', '');
      this.plan = 'once';
    }
    this.buildSteps();
    this.buildTiers();
    this.bindEvents();
    this.render();
    // Appstle injects its radios async; capture the real subscription plan id
    // a moment later so the cart "Upgrade & Save" can reuse the exact same id.
    this.maybePersistPlan();
    setTimeout(() => this.maybePersistPlan(), 2000);
  }

  /* Persist the working Appstle subscription plan id (keyed by product) so the
     cart upsell can convert one-time lines using an id known to be purchasable. */
  maybePersistPlan() {
    if (!this.productId) return;
    const { sub } = this.appstleRadios();
    const planId = (sub && sub.value) || this.sellingPlanId;
    if (planId) {
      try {
        localStorage.setItem('soar:sellingPlan:' + this.productId, planId);
      } catch (e) {
        /* storage unavailable — cart upsell falls back to the Liquid plan id */
      }
    }
  }

  /* ---- builders ---- */
  buildSteps() {
    if (!this.els.steps) return;
    this.els.steps.innerHTML = this.tiers
      .map((t) => {
        const badge = t.badge ? `<span class="bb__step-badge">${t.badge}</span>` : '';
        return `<button type="button" class="bb__step" data-bb-step data-qty="${t.qty}" aria-label="${t.qty} boxes">${badge}<span class="bb__step-dot">${t.qty}x</span></button>`;
      })
      .join('');
  }

  buildTiers() {
    if (!this.els.grid) return;
    this.els.grid.innerHTML = this.tiers
      .map((t) => {
        const badge = t.badge ? `<span class="bb__tier-badge">${t.badge}</span>` : '';
        const orig = this.basePrice * t.qty;
        const disc = this.discountedTotal(t.qty, t);
        const perBar = this.money(this.perBarCents(t));
        const prices =
          disc < orig
            ? `<span class="bb__tier-prices"><s class="bb__tier-orig">${this.money(orig)}</s><span class="bb__tier-disc">${this.money(disc)}</span></span>`
            : `<span class="bb__tier-prices"><span class="bb__tier-disc">${this.money(disc)}</span></span>`;
        return `<button type="button" class="bb__tier" data-bb-tier data-qty="${t.qty}">
          ${badge}
          <span class="bb__tier-label">${t.label}</span>
          ${prices}
          <span class="bb__tier-perbar">${perBar}/bar</span>
        </button>`;
      })
      .join('');
  }

  /* Per-bar price (cents) for a tier at its own quantity. */
  perBarCents(t) {
    const disc = this.discountedTotal(t.qty, t);
    const bars = this.barsReceived(t.qty, t);
    return bars > 0 ? Math.round(disc / bars) : disc;
  }

  bindEvents() {
    this.els.steps?.querySelectorAll('[data-bb-step]').forEach((s) =>
      s.addEventListener('click', () => this.setQty(parseInt(s.dataset.qty, 10)))
    );
    this.els.grid?.querySelectorAll('[data-bb-tier]').forEach((t) =>
      t.addEventListener('click', () => this.setQty(parseInt(t.dataset.qty, 10)))
    );
    this.querySelector('[data-bb-minus]')?.addEventListener('click', () => this.setQty(this.qty - 1));
    this.querySelector('[data-bb-plus]')?.addEventListener('click', () => this.setQty(this.qty + 1));
    this.querySelectorAll('[data-bb-plan]').forEach((b) =>
      b.addEventListener('click', () => this.setPlan(b.dataset.bbPlan))
    );
    this.els.atc?.addEventListener('click', () => this.addToCart());
  }

  /* ---- state ---- */
  setQty(q) {
    this.qty = Math.max(1, q || 1);
    this.render();
  }

  setPlan(p) {
    if (p === 'sub' && !this.sellingPlanId) return;
    this.plan = p;
    this.syncAppstle();
    this.render();
  }

  currentTier() {
    let tier = this.tiers[0];
    this.tiers.forEach((t) => {
      if (this.qty >= t.qty) tier = t;
    });
    return tier;
  }

  /* One-time total in cents for `qty` boxes at `tier` (discount model or flat per-bar). */
  discountedTotal(qty, tier) {
    if (tier && tier.discount != null) {
      return Math.round(this.basePrice * qty * (1 - tier.discount / 100));
    }
    return Math.round((tier ? tier.pricePerBar : 0) * this.barsPerBox * qty);
  }

  /* Bars the customer receives (includes free boxes for the discount model). */
  barsReceived(qty, tier) {
    const bonus = tier && tier.discount != null ? tier.bonus || 0 : 0;
    return (qty + bonus) * this.barsPerBox;
  }

  /* ---- Appstle subscription engine (hidden widget on the page) ---- */
  appstleRadios() {
    let sub = null;
    let once = null;
    document.querySelectorAll('input[name="selling_plan"]').forEach((r) => {
      if (r.value) sub = sub || r;
      else once = once || r;
    });
    return { sub, once };
  }

  /* Keep Appstle's widget selection in sync with our plan toggle. */
  syncAppstle() {
    const { sub, once } = this.appstleRadios();
    const target = this.plan === 'sub' ? sub : once;
    if (target && !target.checked) {
      target.checked = true;
      target.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  /* ---- render ---- */
  render() {
    const tier = this.currentTier();
    const paidBars = this.qty * this.barsPerBox;
    const totalBars = this.barsReceived(this.qty, tier);
    const bonus = tier && tier.discount != null ? tier.bonus || 0 : 0;
    // list price reflects every box received, including free bonus box(es),
    // so e.g. 5x (which ships 6 boxes) strikes through the full 6-box value
    const subtotal = this.basePrice * (this.qty + bonus);
    const discounted = this.discountedTotal(this.qty, tier);
    const subPrice = Math.round(discounted * (1 - this.subExtra / 100));
    const oncePrice = discounted;
    const total = this.plan === 'sub' ? subPrice : oncePrice;
    const youSave = Math.max(subtotal - total, 0);
    const savePct = subtotal > 0 ? Math.round((youSave / subtotal) * 100) : 0;

    // progress — fill the connector line between steps (interpolated)
    let reached = 0;
    this.tiers.forEach((t, i) => {
      if (this.qty >= t.qty) reached = i;
    });
    const nextStep = this.tiers[reached + 1];
    let frac = 0;
    if (nextStep) {
      const span = nextStep.qty - this.tiers[reached].qty;
      frac = span > 0 ? Math.min(Math.max((this.qty - this.tiers[reached].qty) / span, 0), 1) : 0;
    }
    const fillPct = this.tiers.length > 1 ? ((reached + frac) / (this.tiers.length - 1)) * 100 : 100;
    if (this.els.steps) this.els.steps.style.setProperty('--bb-fill', fillPct);
    this.els.steps?.querySelectorAll('[data-bb-step]').forEach((s) => {
      const sq = parseInt(s.dataset.qty, 10);
      s.classList.toggle('is-unlocked', this.qty >= sq);
      s.classList.toggle('is-current', this.qty === sq);
    });

    // nudge
    const next = this.tiers.find((t) => t.qty > this.qty);
    if (this.els.nudge) {
      if (next) {
        const n = next.qty - this.qty;
        this.els.nudge.innerHTML = `Add <strong>${n} more box${n > 1 ? 'es' : ''}</strong> to unlock <strong class="bb__nudge-perk">${next.perk}</strong>`;
      } else {
        this.els.nudge.innerHTML = `🎁 You’ve unlocked <strong class="bb__nudge-perk">${tier.perk}</strong>!`;
      }
    }

    // tier active state
    this.els.grid?.querySelectorAll('[data-bb-tier]').forEach((t) => {
      t.classList.toggle('is-active', this.qty === parseInt(t.dataset.qty, 10));
    });

    // plan states
    this.classList.toggle('bb--once', this.plan === 'once');
    this.querySelectorAll('[data-bb-plan]').forEach((b) => {
      const active = b.dataset.bbPlan === this.plan;
      b.classList.toggle('is-active', active);
      b.setAttribute('aria-selected', active ? 'true' : 'false');
    });
    if (this.els.subTotal) this.els.subTotal.textContent = this.money(subPrice);
    if (this.els.onceTotal) this.els.onceTotal.textContent = this.money(oncePrice);

    // quantity + total
    if (this.els.qty) this.els.qty.textContent = this.qty;
    if (this.els.qtyLabel) {
      const bars = this.qty * this.barsPerBox;
      this.els.qtyLabel.textContent = `${this.qty} Box${this.qty === 1 ? '' : 'es'} — ${bars} Bars`;
    }
    if (this.els.bars) this.els.bars.textContent = paidBars;
    if (this.els.savings) this.els.savings.textContent = `−${this.money(youSave)}`;
    if (this.els.perbar) this.els.perbar.textContent = `${this.money(Math.round(total / totalBars))}/bar`;
    if (this.els.subtotal) {
      this.els.subtotal.textContent = this.money(subtotal);
      this.els.subtotal.hidden = total >= subtotal; // only show the strike when discounted
    }
    if (this.els.total) this.els.total.textContent = this.money(total);
    if (this.els.atcLabel) {
      const save = savePct > 0 ? `<span class="bb__atc-save">Save ${savePct}%</span> ` : '';
      const struck = total < subtotal ? `<s>${this.money(subtotal)}</s> ` : '';
      this.els.atcLabel.innerHTML = `${this.atcText} — ${save}${struck}${this.money(total)}`;
    }
  }

  /* ---- add to cart ---- */
  addToCart() {
    if (!this.variantId) {
      this.showError('No product variant available on this page.');
      return;
    }
    this.setLoading(true);
    this.clearError();

    // Align Appstle's hidden widget to our plan, then use its selling_plan.
    this.syncAppstle();
    let planId = '';
    if (this.plan === 'sub') {
      const { sub } = this.appstleRadios();
      planId = (sub && sub.value) || this.sellingPlanId;
    }

    const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');

    // FormData add (matches Dawn's product form — returns the cart sections).
    const fd = new FormData();
    fd.append('id', this.variantId);
    fd.append('quantity', this.qty);
    if (planId) fd.append('selling_plan', planId);
    if (cart && typeof cart.getSectionsToRender === 'function') {
      fd.append('sections', cart.getSectionsToRender().map((s) => s.id).join(','));
      fd.append('sections_url', window.location.pathname);
    }

    const config = typeof fetchConfig === 'function' ? fetchConfig('javascript') : { method: 'POST', headers: {} };
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    delete config.headers['Content-Type']; // let the browser set the multipart boundary
    config.body = fd;

    const addUrl = (window.routes && window.routes.cart_add_url) || '/cart/add';
    fetch(addUrl, config)
      .then((r) => r.json())
      .then((response) => {
        if (response.status) {
          this.showError(response.description || response.message || 'Could not add to cart.');
          return;
        }
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'bundle-builder', cartData: response });
        }
        if (cart) {
          cart.classList.remove('is-empty');
          try {
            if (typeof cart.renderContents === 'function') cart.renderContents(response);
            else if (typeof cart.open === 'function') cart.open();
          } catch (e) {
            if (typeof cart.open === 'function') cart.open();
          }
        } else {
          window.location = (window.routes && window.routes.cart_url) || '/cart';
        }
      })
      .catch((e) => this.showError(e && e.message ? `Error: ${e.message}` : 'Something went wrong. Please try again.'))
      .finally(() => this.setLoading(false));
  }

  setLoading(on) {
    this.els.atc?.toggleAttribute('aria-disabled', on);
    this.els.atc?.classList.toggle('loading', on);
    this.els.spinner?.classList.toggle('hidden', !on);
  }

  showError(msg) {
    if (!this.els.error) return;
    this.els.error.textContent = msg;
    this.els.error.hidden = false;
  }

  clearError() {
    if (!this.els.error) return;
    this.els.error.hidden = true;
  }

  money(cents) {
    const value = (cents / 100).toFixed(2);
    const m = this.moneyFormat.match(/\{\{\s*(\w+)\s*\}\}/);
    const withCommas = (s) => {
      const p = String(s).split('.');
      p[0] = p[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      return p.join('.');
    };
    if (!m) return `$${value}`;
    const formatted = m[1] === 'amount_no_decimals' ? withCommas((cents / 100).toFixed(0)) : withCommas(value);
    return this.moneyFormat.replace(/\{\{\s*\w+\s*\}\}/, formatted);
  }
}

if (!customElements.get('bundle-builder')) customElements.define('bundle-builder', BundleBuilder);
